// Keep canvas memory bounded regardless of the number of pages in the document.
export function createPdfViewer(viewer, pdf, onError) {
    let disposed = false;
    let rendering = false;
    let activeTask;
    let resizeTimer;
    let width = 0;
    let revision = 0;
    const pages = [];

    function release(entry) {
        if (entry.canvas) {
            entry.canvas.width = 0;
            entry.canvas.height = 0;
            entry.canvas.remove();
            entry.canvas = null;
        }
    }

    function wantedPages() {
        const visualViewport = window.visualViewport;
        const top = visualViewport?.offsetTop || 0;
        const bounds = { top, bottom: top + (visualViewport?.height || window.innerHeight) };
        return pages.filter(({ element }) => {
            const rect = element.getBoundingClientRect();
            return rect.bottom >= bounds.top - 200 && rect.top <= bounds.bottom + 200;
        });
    }

    function nextPage() {
        return wantedPages().find(page => !page.canvas && page.failed !== revision);
    }

    async function renderVisible() {
        if (disposed || !width) return;
        const wanted = wantedPages();
        for (const entry of pages) {
            if (!wanted.includes(entry)) release(entry);
        }
        if (activeTask && !wanted.includes(activeTask.entry)) activeTask.task.cancel();
        if (rendering) return;
        rendering = true;
        try {
            let entry;
            while (!disposed && (entry = nextPage())) {
                const currentRevision = revision;
                const page = await pdf.getPage(entry.number);
                try {
                    if (disposed) return;
                    if (currentRevision !== revision || !wantedPages().includes(entry)) continue;
                    const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width });
                    // Cap both density and total pixels, especially on high-DPI phones.
                    const density = Math.min(window.devicePixelRatio || 1, 1.5,
                        Math.sqrt(2000000 / (viewport.width * viewport.height)));
                    const canvas = document.createElement('canvas');
                    entry.canvas = canvas;
                    canvas.className = 'material-pdf-canvas';
                    canvas.width = Math.max(1, Math.floor(viewport.width * density));
                    canvas.height = Math.max(1, Math.floor(viewport.height * density));
                    canvas.style.width = '100%';
                    const canvasContext = canvas.getContext('2d', { alpha: false });
                    if (!canvasContext) throw new Error('Não foi possível criar a página do PDF.');
                    const task = page.render({ canvasContext, viewport, transform: [density, 0, 0, density, 0, 0] });
                    activeTask = { task, entry };
                    await task.promise;
                    if (!disposed && currentRevision === revision && wantedPages().includes(entry)) {
                        entry.element.replaceChildren(canvas);
                    } else {
                        release(entry);
                    }
                } catch (error) {
                    release(entry);
                    if (!disposed && error.name !== 'RenderingCancelledException') {
                        entry.failed = revision;
                        entry.element.textContent = 'Não foi possível exibir esta página.';
                        onError(error);
                    }
                } finally {
                    activeTask = null;
                    page.cleanup();
                }
            }
        } catch (error) {
            if (!disposed) onError(error);
        } finally {
            rendering = false;
        }
    }

    function resize() {
        if (disposed) return;
        const style = window.getComputedStyle(viewer);
        const nextWidth = Math.floor(viewer.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight));
        if (nextWidth > 0 && width !== nextWidth) {
            width = nextWidth;
            revision += 1;
            if (activeTask) activeTask.task.cancel();
            for (const entry of pages) {
                release(entry);
                entry.element.style.height = `${width * entry.ratio}px`;
            }
        }
        renderVisible();
    }

    const observer = new ResizeObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(resize, 180);
    });
    observer.observe(viewer);
    window.addEventListener('scroll', renderVisible, { passive: true });
    window.visualViewport?.addEventListener('scroll', renderVisible, { passive: true });
    window.visualViewport?.addEventListener('resize', renderVisible, { passive: true });

    async function initialize() {
        try {
            for (let number = 1; number <= pdf.numPages; number += 1) {
                const page = await pdf.getPage(number);
                if (disposed) return;
                const viewport = page.getViewport({ scale: 1 });
                const element = document.createElement('div');
                element.className = 'material-pdf-page';
                element.setAttribute('aria-label', `Página ${number}`);
                const entry = { number, element, ratio: viewport.height / viewport.width, canvas: null };
                pages.push(entry);
                element.style.height = `${width * entry.ratio}px`;
                viewer.appendChild(element);
                // Display the first page without waiting for the rest of the document.
                if (number === 1) resize();
                page.cleanup();
            }
            renderVisible();
        } catch (error) {
            if (!disposed) onError(error);
        }
    }
    initialize();

    return () => {
        disposed = true;
        clearTimeout(resizeTimer);
        observer.disconnect();
        window.removeEventListener('scroll', renderVisible);
        window.visualViewport?.removeEventListener('scroll', renderVisible);
        window.visualViewport?.removeEventListener('resize', renderVisible);
        if (activeTask) activeTask.task.cancel();
        pages.forEach(release);
        viewer.replaceChildren();
    };
}
